<template>
  <div class="payer-chart-table" :class="tableClass">
    <div
      v-for="(bar, index) in chart.bars"
      :key="`payer-chart-row-${index}`"
      class="payer-chart-row"
    >
      <div class="payer-chart-y-label">{{ bar.label }}</div>
      <div class="payer-chart-bar-row">
        <div class="payer-chart-vlines">
          <div
            v-for="(tick, tickIndex) in chart.xTicks"
            :key="`payer-vline-${index}-${tickIndex}`"
            class="payer-chart-vline"
            :style="{ left: `${chart.tickPosition(tick)}%` }"
          ></div>
        </div>
        <div
          class="payer-chart-bar-fill"
          :class="barFillClass"
          :style="{ width: `${bar.widthPct}%` }"
        >
          <span
            v-if="showSector && bar.sector"
            class="payer-chart-bar-label"
          >{{ bar.sector }}</span>
          <span
            v-else-if="showBarValues && bar.widthPct >= 12"
            class="risk-performance-bar-label"
          >{{ formatPercentTick(bar.value) }}</span>
        </div>
      </div>
    </div>

    <div class="payer-chart-x-axis-row">
      <div class="payer-chart-x-axis-spacer"></div>
      <div class="payer-chart-x-axis">
        <span
          v-for="(tick, index) in chart.xTicks"
          :key="`payer-x-${index}`"
          class="payer-chart-x-tick"
          :style="{ left: `${chart.tickPosition(tick)}%` }"
        >{{ formatXTick(tick) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PayerBarChart',
  props: {
    chart: {
      type: Object,
      required: true
    },
    labelWidth: {
      type: String,
      default: 'default'
    },
    barColor: {
      type: String,
      default: 'orange'
    },
    xTickType: {
      type: String,
      default: 'percent'
    },
    showSector: {
      type: Boolean,
      default: false
    },
    showBarValues: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    tableClass() {
      if (this.labelWidth === 'narrow') return 'payer-chart-table--narrow';
      if (this.labelWidth === 'risk') return 'payer-chart-table--risk';
      return '';
    },
    barFillClass() {
      const classes = [];

      if (this.barColor === 'blue') {
        classes.push('payer-chart-bar-fill--blue');
      }

      if (this.showBarValues) {
        classes.push('risk-performance-bar-fill');
      }

      return classes;
    }
  },
  methods: {
    formatXTick(value) {
      if (this.xTickType === 'currency') {
        const number = Number(value);
        if (Number.isNaN(number)) return '0';
        return number.toLocaleString('en-US');
      }

      const number = Number(value);
      if (Number.isNaN(number)) return '0.00%';
      return `${number.toFixed(2)}%`;
    },
    formatPercentTick(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return '0.00%';
      return `${number.toFixed(2)}%`;
    }
  }
};
</script>

<style lang="scss" scoped>
.payer-chart-table {
  --payer-label-width: 39%;
}

.payer-chart-table--narrow {
  --payer-label-width: 28%;
}

.payer-chart-table--risk {
  --payer-label-width: 42%;
}

.payer-chart-row,
.payer-chart-x-axis-row {
  display: grid;
  grid-template-columns: var(--payer-label-width) 1fr;
  column-gap: 10px;
}

.payer-chart-row {
  margin-bottom: 10px;
}

.payer-chart-y-label {
  display: block;
  min-height: 22px;
  font-size: 9px;
  line-height: 1.25;
  color: #111827;
  word-break: break-word;
  overflow: hidden;
}

.payer-chart-bar-row {
  position: relative;
  height: 22px;
}

.payer-chart-vlines {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.payer-chart-vline {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: #d1d5db;
  transform: translateX(-0.5px);
}

.payer-chart-bar-fill {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: #ed7d31;
  min-width: 0;

  &--blue {
    background: #4472c4;
  }
}

.payer-chart-bar-label {
  padding: 0 6px;
  font-size: 8px;
  font-weight: 600;
  line-height: 1.1;
  color: #ffffff;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.risk-performance-bar-fill {
  justify-content: flex-end;
}

.risk-performance-bar-label {
  padding: 0 8px;
  font-size: 9px;
  font-weight: 600;
  line-height: 1.1;
  color: #ffffff;
  white-space: nowrap;
}

.payer-chart-x-axis-row {
  margin-top: 6px;
}

.payer-chart-x-axis-spacer {
  min-height: 0;
}

.payer-chart-x-axis {
  position: relative;
  height: 16px;
}

.payer-chart-x-tick {
  position: absolute;
  font-size: 9px;
  color: #374151;
  white-space: nowrap;
  transform: translateX(-50%);

  &:first-child {
    transform: translateX(0);
  }

  &:last-child {
    transform: translateX(-100%);
  }
}
</style>
